import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Modal,
  TextField,
  Stack,
  Tooltip
} from "@mui/material";
import {
  FiGrid,
  FiList,
  FiFilter,
  FiMoreVertical,
  FiEye,
  FiPackage,
  FiArrowLeft
} from "react-icons/fi";
import { getData, putData } from "../../Webservice/webservice";
import { IoMdPrint } from "react-icons/io";
import { FaArrowRightToBracket } from "react-icons/fa6";

// Shared status configuration function
const getStatusConfig = status => {
  switch (status) {
    case "delivered":
      return {
        color: "#4caf50",
        icon: <FiPackage size={24} />,
        label: "Delivered"
      };
    case "shipped":
      return {
        color: "#2196f3",
        icon: <FiPackage size={24} />,
        label: "Shipped"
      };
    case "packed":
      return {
        color: "#ff9800",
        icon: <FiPackage size={24} />,
        label: "Packed"
      };
    case "accepted":
      return {
        color: "#3f51b5",
        icon: <FiPackage size={24} />,
        label: "Accepted"
      };
    case "pending":
      return {
        color: "#f44336",
        icon: <FiPackage size={24} />,
        label: "Pending"
      };
    case "rejected":
      return {
        color: "#9e9e9e",
        icon: <FiPackage size={24} />,
        label: "Rejected"
      };
    default:
      return {
        color: "#9e9e9e",
        icon: <FiPackage size={24} />,
        label: "Unknown"
      };
  }
};

const OnlineOrders = () => {
  const navigate = useNavigate();
  const { requestId: requestIdParam } = useParams();
  const [view, setView] = useState("grid");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("pending");
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openPrintModal, setOpenPrintModal] = useState(false);
  const [openPrintLabelModal, setOpenPrintLabelModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  // List of available statuses for tabs
  const statuses = ["pending", "accepted", "packed", "shipped", "delivered", "rejected"];

  // Fetch orders from API based on selected status
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await getData(
          `vendor/request/list?status=${selectedStatus}`,
          null,
          token
        );
        const { requests } = response;

        // Log the API response to debug the structure
        console.log("API Response (requests):", requests);
        if (requests.length > 0) {
          console.log("First request object keys:", Object.keys(requests[0]));
        }

        const mappedOrders = requests.map(request => ({
          id: request.receiptId,
          requestId: request._id,
          email: request.vendorId.email,
          mobile: request.vendorId.mobile,
          date: new Date(request.createdAt).toISOString().split("T")[0],
          status: request.status,
          etaDays: request.orderId.etaDays,
          items: request.quantity,
          paymentStatus: "pending",
          address: "N/A",
          product: {
            title: request.product_id.title,
            upc: request.product_id.upc,
            ownership: request.product_id.ownership,
            product_images: request.product_id.product_images,
            unitType: request.product_id.unitType,
            unitValue: request.product_id.unitValue,
            unitName: request.product_id.unitName,
            brand: request.product_id.brand,
            container_type: request.product_id.container_type,
            isFree: request.product_id.isFree
          },
          warehouse: {
            name: request.orderId.warehouseID.name,
            code: request.orderId.warehouseID.code,
            address: request.orderId.warehouseID.address,
            city: request.orderId.warehouseID.city,
            state: request.orderId.warehouseID.state,
            country: request.orderId.warehouseID.country,
            pincode: request.orderId.warehouseID.pincode,
            contactPerson: request.orderId.warehouseID.contactPerson,
            phoneNumber: request.orderId.warehouseID.phoneNumber,
            email: request.orderId.warehouseID.email
          }
        }));

        setOrders(mappedOrders);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch orders. Please try again later.");
        setLoading(false);
      }
    };

    fetchOrders();
  }, [selectedStatus]);

  // When URL has a requestId, try to select that order. Search across all statuses if needed.
  useEffect(() => {
    const selectFromCurrentOrders = () => {
      if (!requestIdParam) return false;
      const match = orders.find(o => o.requestId === requestIdParam);
      if (match) {
        setSelectedOrder(match);
        return true;
      }
      return false;
    };

    const searchAcrossStatuses = async () => {
      if (!requestIdParam) return;
      // try current first
      if (selectFromCurrentOrders()) return;
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      for (const status of statuses) {
        try {
          const res = await getData(`vendor/request/list?status=${status}`, null, token);
          const list = (res && res.requests) || [];
          const mapped = list.map(request => ({
            id: request.receiptId,
            requestId: request._id,
            email: request.vendorId.email,
            mobile: request.vendorId.mobile,
            date: new Date(request.createdAt).toISOString().split("T")[0],
            status: request.status,
            etaDays: request.orderId.etaDays,
            items: request.quantity,
            paymentStatus: "pending",
            address: "N/A",
            product: {
              title: request.product_id.title,
              upc: request.product_id.upc,
              ownership: request.product_id.ownership,
              product_images: request.product_id.product_images,
              unitType: request.product_id.unitType,
              unitValue: request.product_id.unitValue,
              unitName: request.product_id.unitName,
              brand: request.product_id.brand,
              container_type: request.product_id.container_type,
              isFree: request.product_id.isFree
            },
            warehouse: {
              name: request.orderId.warehouseID.name,
              code: request.orderId.warehouseID.code,
              address: request.orderId.warehouseID.address,
              city: request.orderId.warehouseID.city,
              state: request.orderId.warehouseID.state,
              country: request.orderId.warehouseID.country,
              pincode: request.orderId.warehouseID.pincode,
              contactPerson: request.orderId.warehouseID.contactPerson,
              phoneNumber: request.orderId.warehouseID.phoneNumber,
              email: request.orderId.warehouseID.email
            }
          }));
          const found = mapped.find(o => o.requestId === requestIdParam);
          if (found) {
            setSelectedStatus(status);
            setOrders(mapped); // show that status list
            setSelectedOrder(found);
            return;
          }
        } catch (e) {
          // ignore and continue
        }
      }
      // not found anywhere
      setError("Order not found for provided id.");
    };

    // trigger on initial mount and when orders change while param exists
    if (requestIdParam) {
      searchAcrossStatuses();
    } else {
      setSelectedOrder(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestIdParam]);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const handleStatusChange = (event, newStatus) => {
    if (newStatus !== null) {
      setSelectedStatus(newStatus);
    }
  };

  // Handle reject order
  const handleRejectOrder = async () => {
    if (!selectedOrder?.requestId) {
      setError("Cannot reject order: Request ID is missing.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      const payload = {
        status: "rejected",
        rejectionReason: rejectionReason
      };
      const response = await putData(`vendor/request/update/${selectedOrder.requestId}`, payload, token);
      if (response) {
        setOrders(orders.map(o => o.id === selectedOrder.id ? { ...o, status: "rejected" } : o));
        setSelectedOrder({ ...selectedOrder, status: "rejected" });
        setSuccess("Order rejected successfully!");
        setOpenRejectModal(false);
        setRejectionReason("");
      } else {
        setError("Failed to update order status. Please try again.");
      }
      setLoading(false);
    } catch (err) {
      setError("Failed to update order status. Please try again later.");
      setLoading(false);
    }
  };

  // Handle print invoice
  const handlePrintInvoice = async () => {
    if (!selectedOrder?.requestId) {
      setError("Cannot print invoice: Request ID is missing.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      const response = await putData(`vendor/request/print/${selectedOrder.requestId}`, {}, token);
      
      if (response) {
        window.open(response.pdfPath, '_blank');
        setSuccess("Invoice opened successfully!");
        setOpenPrintModal(false);
      } else {
        setError("Failed to generate invoice. Please try again.");
      }
      setLoading(false);
    } catch (err) {
      setError("Failed to generate invoice. Please try again later.");
      setLoading(false);
    }
  };

  // Handle print label
  const handlePrintLabel = async () => {
    if (!selectedOrder?.requestId) {
      setError("Cannot print label: Request ID is missing.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      const response = await putData(`vendor/request/print/del/${selectedOrder.requestId}`, {}, token);
      
      if (response && response.pdfPath) {
        window.open(response.pdfPath, '_blank');
        setSuccess("Label opened successfully!");
        setOpenPrintLabelModal(false);
      } else {
        setError("Failed to generate label. Please try again.");
      }
      setLoading(false);
    } catch (err) {
      setError("Failed to generate label. Please try again later.");
      setLoading(false);
    }
  };

  // Handle open reject modal
  const handleOpenRejectModal = (order) => {
    setSelectedOrder(order);
    setOpenRejectModal(true);
    setRejectionReason("");
  };

  // Handle close reject modal
  const handleCloseRejectModal = () => {
    setOpenRejectModal(false);
    setRejectionReason("");
  };

  // Handle open print modal
  const handleOpenPrintModal = (order) => {
    setSelectedOrder(order);
    setOpenPrintModal(true);
  };

  // Handle close print modal
  const handleClosePrintModal = () => {
    setOpenPrintModal(false);
  };

  // Handle open print label modal
  const handleOpenPrintLabelModal = (order) => {
    setSelectedOrder(order);
    setOpenPrintLabelModal(true);
  };

  // Handle close print label modal
  const handleClosePrintLabelModal = () => {
    setOpenPrintLabelModal(false);
  };

  // Order Card Component for Grid View
  const handleOpenDetails = (order) => {
    setSelectedOrder(order);
    if (order?.requestId) {
      navigate(`/orders/${order.requestId}`);
    }
  };

  const OrderCard = ({ order }) => {
    const statusConfig = getStatusConfig(order.status);
    const imageUrl = order.product?.product_images?.[0] || "/img/default-product.png";

    return (
      <Paper
        onClick={() => handleOpenDetails(order)}
        sx={{
          borderRadius: "20px",
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          cursor: "pointer",
          border: "1px solid #f1f5f9",
          backgroundColor: "#ffffff",
          "&:hover": {
            transform: "translateY(-6px)",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            borderColor: "#e2e8f0"
          }
        }}
      >
        <Box sx={{ position: "relative", height: 160,width:"215px", backgroundColor: "#f8fafc" }}>
          <img
            src={imageUrl}
            alt={order.product?.title || "Product"}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        <Box
          sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 1
            }}
          >
            <Chip
              label={statusConfig.label}
              size="small"
              sx={{
                fontWeight: 800,
                fontSize: "0.65rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                backgroundColor: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(4px)",
                color: statusConfig.color,
                border: `1px solid ${statusConfig.color}40`,
                height: 24
          }}
            />
          </Box>
        </Box>

        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: "#64748b", 
                fontWeight: 700, 
                display: "block", 
                mb: 0.5,
                fontSize: "0.7rem",
                letterSpacing: "0.5px"
              }}
            >
              ORDER #{order.id}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 700, 
                color: "#1e293b", 
                fontFamily: "Mulish, sans-serif",
                lineHeight: 1.3,
                // height: "2.6em",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical"
              }}
            >
              {order.product?.title}
            </Typography>
          </Box>

          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 32, 
                height: 32, 
                borderRadius: "8px", 
                backgroundColor: "#f1f5f9", 
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b"
              }}
            >
              <FiPackage size={16} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", fontSize: "0.65rem" }}>Customer/WH</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>{order.warehouse?.contactPerson}</Typography>
          </Box>
        </Box> */}

          <Divider sx={{ my: 2, borderColor: "#f1f5f9" }} />

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>Quantity</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>{order.items} Units</Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>Date</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>{order.date || "Today"}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Paper>
    );
  };

  // Order Table Component for Table View
  const OrderTable = () => (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
        overflow: "hidden",
        marginTop: "20px"
      }}
    >
      <Table sx={{ minWidth: 650 }} aria-label="orders table">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#f8f9fa",
              "& th": {
                fontFamily: "Mulish, sans-serif",
                fontWeight: 700,
                fontSize: "0.875rem",
                color: "#495057",
                borderBottom: "2px solid #e9ecef",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                py: 2
              }
            }}
          >
            <TableCell align="center">Image</TableCell>
            <TableCell>Order ID</TableCell>
            <TableCell>Customer / Warehouse</TableCell>
            <TableCell align="center">Date</TableCell>
            <TableCell align="center">Items</TableCell>
            <TableCell align="center">ETA Days</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map(order => {
            const statusConfig = getStatusConfig(order.status);
            const imageUrl = order.product?.product_images?.[0] || "/img/default-product.png";

            return (
              <TableRow
                key={order.requestId}
                hover
                sx={{
                  "&:last-child td": { borderBottom: 0 },
                  "& td": {
                    py: 1.5,
                    borderBottom: "1px solid #f1f3f5",
                    fontFamily: "Mulish, sans-serif"
                  }
                }}
              >
                <TableCell align="center">
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "8px",
                      overflow: "hidden",
                      mx: "auto",
                      border: "1px solid #dee2e6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#fff"
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={order.product?.title || "Product"}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography
                    fontWeight={600}
                    variant="body2"
                    sx={{ color: "#212529", fontSize: "0.95rem" }}
                  >
                    {order.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    fontWeight={600}
                    variant="body2"
                    sx={{ color: "#212529", fontSize: "0.95rem" }}
                  >
                    {order.warehouse?.contactPerson || "N/A"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {order.warehouse?.name || "N/A"}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{order.date}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600}>
                    {order.items}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{order.etaDays}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={statusConfig.label}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      textTransform: "capitalize",
                      backgroundColor: `${statusConfig.color}15`,
                      color: statusConfig.color,
                      fontFamily: "Mulish, sans-serif",
                      px: 1,
                      height: 28
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDetails(order)}
                        sx={{
                          color: "#0d6efd",
                          backgroundColor: "rgba(13, 110, 253, 0.05)",
                          "&:hover": { backgroundColor: "rgba(13, 110, 253, 0.15)" }
                        }}
                      >
                        <FiEye size={18} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Order Details View
  const OrderDetailsView = ({ order }) => {
    const statusConfig = getStatusConfig(order.status);

    const handleAcceptOrder = async () => {
      if (!order.requestId) {
        setError("Cannot accept order: Request ID is missing.");
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setSuccess(null);
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const payload = { status: "accepted" };
        const response = await putData(`vendor/request/update/${order.requestId}`, payload, token);
        if (response) {
          setOrders(orders.map(o => o.id === order.id ? { ...o, status: "accepted" } : o));
          setSelectedOrder({ ...order, status: "accepted" });
          setSuccess("Order accepted successfully!");
        } else {
          setError("Failed to update order status. Please try again.");
        }
        setLoading(false);
      } catch (err) {
        setError("Failed to update order status. Please try again later.");
        setLoading(false);
      }
    };

    const handleMoveToPacked = async () => {
      if (!order.requestId) {
        setError("Cannot move to packed: Request ID is missing.");
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setSuccess(null);
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const payload = { status: "packed" };
        const response = await putData(`vendor/request/update/${order.requestId}`, payload, token);
        if (response) {
          setOrders(orders.map(o => o.id === order.id ? { ...o, status: "packed" } : o));
          setSelectedOrder({ ...order, status: "packed" });
          setSuccess("Order moved to packed successfully!");
        } else {
          setError("Failed to update order status to packed. Please try again.");
        }
        setLoading(false);
      } catch (err) {
        setError("Failed to update order status to packed. Please try again later.");
        setLoading(false);
      }
    };


     const handleMoveShipped = async () => {
      if (!order.requestId) {
        setError("Cannot move to Shipped: Request ID is missing.");
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setSuccess(null);
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const payload = { status: "shipped" };
        const response = await putData(`vendor/request/update/${order.requestId}`, payload, token);
        if (response) {
          setOrders(orders.map(o => o.id === order.id ? { ...o, status: "shipped" } : o));
          setSelectedOrder({ ...order, status: "shipped" });
          setSuccess("Order moved to shipped successfully!");
        } else {
          setError("Failed to update order status to shipped. Please try again.");
        }
        setLoading(false);
      } catch (err) {
        setError("Failed to update order status to shipped. Please try again later.");
        setLoading(false);
      }
    };

    return (
      <Box sx={{ p: 3, backgroundColor: "#f8fafc", minHeight: "100vh" }}>
        {/*order details view */}
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<FiArrowLeft />}
            onClick={() => { setSelectedOrder(null); navigate("/orders"); }}
            sx={{
              mb: 2,
              fontFamily: "Mulish, sans-serif",
              color: "#64748b",
              "&:hover": {
                backgroundColor: "rgba(100, 116, 139, 0.04)",
              }
            }}
          >
            Back to Orders
          </Button>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  color: "#1e293b",
                  fontFamily: "Mulish, sans-serif",
                  fontWeight: 700,
                  mb: 0.5
                }}
              >
                Order #{order.id}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#64748b", fontFamily: "Mulish, sans-serif" }}
              >
                Order placed • {order.date || "Recent"}
              </Typography>
            </Box>
            <Chip
              label={statusConfig.label}
              sx={{
                backgroundColor: statusConfig.color + "20",
                color: statusConfig.color,
                fontWeight: 600,
                fontSize: "0.875rem",
                px: 1,
                py: 0.5,
                border: `1px solid ${statusConfig.color}40`
              }}
            />
          </Box>
        </Box>

        {/* Success/Error Display */}
        {success && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          </Box>
        )}
        {error && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {/* Main Content Grid */}
        <Box sx={{ display: "grid", gap: 3 }}>
          {/* Order Summary Card */}
          <Paper sx={{ p: 3, borderRadius: "12px", boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)" }}>
            <Typography
              variant="h6"
              sx={{
                color: "#1e293b",
                fontFamily: "Mulish, sans-serif",
                fontWeight: 600,
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1
              }}
            >
              📦 Order Summary
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                  ITEMS
                </Typography>
                <Typography variant="body1" sx={{ color: "#1e293b", fontWeight: 500 }}>
                  {order.items}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                  ESTIMATED DELIVERY
                </Typography>
                <Typography variant="body1" sx={{ color: "#1e293b", fontWeight: 500 }}>
                  {order.etaDays} days
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                  STATUS
                </Typography>
                <Typography variant="body1" sx={{ color: "#1e293b", fontWeight: 500 }}>
                  {statusConfig.label}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Product Details Card */}
          <Paper sx={{ p: 3, borderRadius: "12px", boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)" }}>
            <Typography
              variant="h6"
              sx={{
                color: "#1e293b",
                fontFamily: "Mulish, sans-serif",
                fontWeight: 600,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1
              }}
            >
              🛍️ Product Details
            </Typography>
            
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
              {/* Product Info */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600, mb: 1 }}>
                  PRODUCT INFORMATION
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <InfoRow label="Title" value={order.product.title} />
                  <InfoRow label="UPC" value={order.product.upc} />
                  <InfoRow label="Brand" value={order.product.brand} />
                  <InfoRow label="Ownership" value={order.product.ownership} />
                </Box>
              </Box>

              {/* Product Specifications */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600, mb: 1 }}>
                  SPECIFICATIONS
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <InfoRow label="Unit Type" value={order.product.unitType} />
                  <InfoRow label="Unit Value" value={order.product.unitValue} />
                  <InfoRow label="Unit Name" value={order.product.unitName} />
                  <InfoRow label="Container Type" value={order.product.container_type} />
                  <InfoRow label="Free Product" value={order.product.isFree ? "Yes" : "No"} />
                </Box>
              </Box>
            </Box>

            {/* Product Images */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600, mb: 2 }}>
                PRODUCT IMAGES
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {order.product.product_images.map((image, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: "relative",
                      width: "100px",
                      height: "100px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid #e2e8f0",
                      cursor: "pointer",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "scale(1.05)",
                      }
                    }}
                  >
                    <img
                      src={image}
                      alt={`Product ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>

          {/* Warehouse Information Card */}
          <Paper sx={{ p: 3, borderRadius: "12px", boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)" }}>
            <Typography
              variant="h6"
              sx={{
                color: "#1e293b",
                fontFamily: "Mulish, sans-serif",
                fontWeight: 600,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1
              }}
            >
              🏭 Warehouse Information
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
              {/* Warehouse Details */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600, mb: 1 }}>
                  WAREHOUSE DETAILS
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <InfoRow label="Name" value={order.warehouse.name} />
                  <InfoRow label="Code" value={order.warehouse.code} />
                  <InfoRow label="Contact Person" value={order.warehouse.contactPerson} />
                </Box>
              </Box>

              {/* Address & Contact */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600, mb: 1 }}>
                  CONTACT & LOCATION
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <InfoRow label="Address" value={order.warehouse.address} />
                  <InfoRow label="City" value={order.warehouse.city} />
                  <InfoRow label="State" value={order.warehouse.state} />
                  <InfoRow label="Country" value={order.warehouse.country} />
                  <InfoRow label="Pincode" value={order.warehouse.pincode} />
                  <InfoRow label="Phone" value={order.warehouse.phoneNumber} />
                  <InfoRow label="Email" value={order.warehouse.email} />
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}>
            {order.status === "pending" && (
              <Button
                onClick={() => handleOpenRejectModal(order)}
                disabled={loading || order.status !== "pending"}
                sx={{
                  border: "1px solid black",
                  color: "white",
                  backgroundColor: "#2B333A",
                  "&:hover": { backgroundColor: "#2B333A" },
                  fontFamily: "Mulish, sans-serif",
                  textTransform: "none",
                  fontWeight: 600
                }}
              >
                {loading ? <CircularProgress size={24} /> : "Reject Order"}
              </Button>
            )}
            {order.status === "pending" && (
              <Button
                onClick={handleAcceptOrder}
                disabled={loading || order.status !== "pending" || !order.requestId}
                sx={{
                  border: "1px solid black",
                  color: "white",
                  backgroundColor: "#2B333A",
                  "&:hover": { backgroundColor: "#2B333A" },
                  fontFamily: "Mulish, sans-serif",
                  textTransform: "none",
                  fontWeight: 600
                }}
              >
                {loading ? <CircularProgress size={24} /> : "Accept Order"}
              </Button>
            )}
            {order.status === "accepted" && (
              <Button
                onClick={() => handleOpenPrintModal(order)}
                disabled={loading || order.status !== "accepted"}
                startIcon={!loading && <IoMdPrint size={18} />}
                sx={{
                  px: 2.5,
                  py: 1,
                  minWidth: 160,
                  borderRadius: "8px",
                  border: "1px solid #2B333A",
                  backgroundColor: "#2B333A",
                  color: "#fff",
                  fontFamily: "Mulish, sans-serif",
                  fontWeight: 600,
                  textTransform: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,

                  "&:hover": {
                    backgroundColor: "#1F262C"
                  },

                  "&.Mui-disabled": {
                    backgroundColor: "#9CA3AF",
                    borderColor: "#9CA3AF",
                    color: "#fff",
                    cursor: "not-allowed"
                  }
                }}
              >
               Print Invoice
              </Button>

            )}
            {order.status === "accepted" && (
             <Button
                  onClick={handleMoveToPacked}
                  disabled={loading || order.status !== "accepted"}
                  endIcon={!loading && <FaArrowRightToBracket size={18} />}
                  sx={{
                    px: 2.5,
                    py: 1,
                    minWidth: 180,
                    borderRadius: "8px",
                    border: "1px solid #2B333A",
                    backgroundColor: "#2B333A",
                    color: "#fff",
                    fontFamily: "Mulish, sans-serif",
                    fontWeight: 600,
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,

                    "&:hover": {
                      backgroundColor: "#1F262C"
                    },

                    "&.Mui-disabled": {
                      backgroundColor: "#9CA3AF",
                      borderColor: "#9CA3AF",
                      color: "#fff",
                      cursor: "not-allowed"
                    }
                  }}
                >
                  Move To Packed
                </Button>

            )}
            {order.status === "packed" && (
              <Button
                onClick={() => handleOpenPrintLabelModal(order)}
                disabled={loading || order.status !== "packed"}
                 sx={{
                    px: 2.5,
                    py: 1,
                    minWidth: 180,
                    borderRadius: "8px",
                    border: "1px solid #2B333A",
                    backgroundColor: "#2B333A",
                    color: "#fff",
                    fontFamily: "Mulish, sans-serif",
                    fontWeight: 600,
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,

                    "&:hover": {
                      backgroundColor: "#1F262C"
                    },

                    "&.Mui-disabled": {
                      backgroundColor: "#9CA3AF",
                      borderColor: "#9CA3AF",
                      color: "#fff",
                      cursor: "not-allowed"
                    }
                  }}
              >
                <IoMdPrint size={20} />
                {loading ? <CircularProgress size={24} /> : "Print Label"}
              </Button>
            )}
            {order.status === "packed" && (
              <Button
                onClick={handleMoveShipped}
                disabled={loading || order.status !== "packed"}
                 sx={{
                    px: 2.5,
                    py: 1,
                    minWidth: 180,
                    borderRadius: "8px",
                    border: "1px solid #2B333A",
                    backgroundColor: "#2B333A",
                    color: "#fff",
                    fontFamily: "Mulish, sans-serif",
                    fontWeight: 600,
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,

                    "&:hover": {
                      backgroundColor: "#1F262C"
                    },

                    "&.Mui-disabled": {
                      backgroundColor: "#9CA3AF",
                      borderColor: "#9CA3AF",
                      color: "#fff",
                      cursor: "not-allowed"
                    }
                  }}
              >
                
                {loading ? <CircularProgress size={24} /> : "Move To Shipped"}
                <FaArrowRightToBracket size={20}  />
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // Reusable Info Row Component
  const InfoRow = ({ label, value }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500, minWidth: "120px" }}>
        {label}
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          color: "#1e293b", 
          fontWeight: 500, 
          textAlign: "right",
          flex: 1,
          wordBreak: "break-word"
        }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2, backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Reject Order Modal */}
      <Modal
        open={openRejectModal}
        onClose={handleCloseRejectModal}
        aria-labelledby="reject-order-modal"
        aria-describedby="reject-order-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 400 },
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          p: 3,
        }}>
          <Typography
            id="reject-order-modal"
            variant="h6"
            sx={{
              fontFamily: "Mulish, sans-serif",
              fontWeight: 600,
              mb: 2,
            }}
          >
            Reject Order #{selectedOrder?.id}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Status"
              value="rejected"
              disabled
              fullWidth
              sx={{ fontFamily: "Mulish, sans-serif" }}
            />
            <TextField
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              multiline
              rows={4}
              fullWidth
              required
              sx={{ fontFamily: "Mulish, sans-serif" }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                onClick={handleCloseRejectModal}
                sx={{
                  fontFamily: "Mulish, sans-serif",
                  textTransform: 'none',
                  color: '#64748b',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRejectOrder}
                disabled={loading || !rejectionReason.trim()}
                sx={{
                  fontFamily: "Mulish, sans-serif",
                  textTransform: 'none',
                  backgroundColor: "#2B333A",
                  color: 'white',
                  '&:hover': { backgroundColor: "#2B333A" },
                  fontWeight: 600,
                }}
              >
                {loading ? <CircularProgress size={24} /> : "Submit"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Print Invoice Modal */}
      <Modal
        open={openPrintModal}
        onClose={handleClosePrintModal}
        aria-labelledby="print-invoice-modal"
        aria-describedby="print-invoice-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 400 },
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          p: 3,
        }}>
          <Typography
            id="print-invoice-modal"
            variant="h6"
            sx={{
              fontFamily: "Mulish, sans-serif",
              fontWeight: 600,
              mb: 2,
            }}
          >
            Print Invoice for Order #{selectedOrder?.id}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Click below to generate and open the invoice PDF.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                onClick={handleClosePrintModal}
                sx={{
                  fontFamily: "Mulish, sans-serif",
                  textTransform: 'none',
                  color: '#64748b',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePrintInvoice}
                disabled={loading}
                sx={{
                  fontFamily: "Mulish, sans-serif",
                  textTransform: 'none',
                  backgroundColor: "#2B333A",
                  color: 'white',
                  '&:hover': { backgroundColor: "#2B333A" },
                  fontWeight: 600,
                }}
              >
                {loading ? <CircularProgress size={24} /> : "Print Invoice"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Print Label Modal */}
      <Modal
        open={openPrintLabelModal}
        onClose={handleClosePrintLabelModal}
        aria-labelledby="print-label-modal"
        aria-describedby="print-label-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 400 },
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          p: 3,
        }}>
          <Typography
            id="print-label-modal"
            variant="h6"
            sx={{
              fontFamily: "Mulish, sans-serif",
              fontWeight: 600,
              mb: 2,
            }}
          >
            Print Label for Order #{selectedOrder?.id}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Click below to generate and open the label PDF.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                onClick={handleClosePrintLabelModal}
                sx={{
                  fontFamily: "Mulish, sans-serif",
                  textTransform: 'none',
                  color: '#64748b',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePrintLabel}
                disabled={loading}
                sx={{
                  fontFamily: "Mulish, sans-serif",
                  textTransform: 'none',
                  backgroundColor: "rgb(255 209 2)",
                  color: 'black',
                  '&:hover': { backgroundColor: "rgb(255 209 2)" },
                  fontWeight: 600,
                }}
              >
                {loading ? <CircularProgress size={24} /> : "Print Label"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {selectedOrder ? (
        <OrderDetailsView order={selectedOrder} />
      ) : (
        <>
          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography
                variant="h5"
                component="h1"
                sx={{ 
                  fontFamily: 'Mulish, sans-serif', 
                  fontWeight: 700, 
                  color: "#1a1a1a", 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1 
                }}
              >
                Online Orders
                <Chip
                  label={`${orders.length} ${orders.length === 1 ? 'order' : 'orders'}`}
                  size="small"
                  sx={{ 
                    color: '#d32f2f', 
                    bgcolor: 'rgba(211, 47, 47, 0.1)',
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 700,
                    borderRadius: '6px',
                    height: 24
                  }}
                />
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                {/* <Button
                  variant="outlined"
                  startIcon={<FiFilter />}
                  sx={{
                    fontFamily: 'Mulish, sans-serif',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#dee2e6',
                    color: '#495057',
                    '&:hover': { 
                      borderColor: '#adb5bd',
                      backgroundColor: '#f8f9fa' 
                    }
                  }}
                >
                  Filters
                </Button> */}

                <ToggleButtonGroup
                  value={view}
                  exclusive
                  onChange={handleViewChange}
                  aria-label="view toggle"
                >
                  <ToggleButton value="grid" aria-label="grid view">
                    <FiGrid size={20} />
                  </ToggleButton>
                  <ToggleButton value="table" aria-label="table view">
                    <FiList size={20} />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            {/* Status Tabs */}
            <Box sx={{ mb: 3 }}>
              <Tabs
                value={selectedStatus}
                onChange={handleStatusChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  '& .MuiTabs-indicator': {
                    display: 'none'
                  },
                  '& .MuiTabs-scroller': {
                    padding: '8px 0'
                  }
                }}
              >
                {statuses.map((status) => (
                  <Tab
                    key={status}
                    label={getStatusConfig(status).label}
                    value={status}
                    sx={{
                      fontFamily: 'Mulish, sans-serif',
                      fontWeight: 800,
                      textTransform: 'capitalize',
                      borderRadius: '8px',
                      mx: 0.5,
                      py: 1,
                      px: 2,
                      color: selectedStatus === status ? getStatusConfig(status).color : 'text.secondary',
                      backgroundColor: selectedStatus === status ? `${getStatusConfig(status).color}20` : 'transparent',
                      border: '1px solid',
                      borderColor: selectedStatus === status ? getStatusConfig(status).color : 'divider',
                      '&.Mui-selected': {
                        color: getStatusConfig(status).color,
                        backgroundColor: `${getStatusConfig(status).color}20`,
                        borderColor: getStatusConfig(status).color
                      },
                      '&:hover': {
                        backgroundColor: `${getStatusConfig(status).color}30`
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          </Box>

          {/* Orders Content */}
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ py: 8 }}
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ py: 8 }}
            >
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : orders.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              sx={{ py: 10, opacity: 0.8 }}
            >
              <img 
                src="/img/nodata.png" 
                alt="no data" 
                style={{ width: 140, marginBottom: "24px", opacity: 0.8 }} 
              />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#495057', mb: 1, fontFamily: 'Mulish, sans-serif' }}>
                No orders found
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'Mulish, sans-serif' }}>
                {selectedStatus ? `There are no ${selectedStatus} orders at the moment.` : "Try adjusting your search or filters"}
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => setSelectedStatus("pending")}
                sx={{ mt: 3, textTransform: 'none', borderRadius: '8px', fontFamily: 'Mulish, sans-serif' }}
              >
                View Pending Orders
              </Button>
            </Box>
          ) : view === "grid" ? (
            <Grid container spacing={3}>
              {orders.map(order => (
                <Grid item xs={12} sm={6} md={3} key={order.id}>
                  <OrderCard order={order} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <OrderTable />
          )}
        </>
      )}
    </Box>
  );
};

export default OnlineOrders;